import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import { errMsg, fmtTime } from "@/lib/api";
import { card, Empty } from "@/components/admin/kit";
import { useGet } from "@/components/subs/common";

const LABEL_CLS = { "Waiting": "bg-gray-100 text-gray-700", "Being checked": "bg-amber-100 text-amber-800", "Calls started": "bg-green-100 text-green-800", "Needs new photo": "bg-red-100 text-red-700", "Duplicate": "bg-purple-100 text-purple-800" };

function Retake({ api, s, onDone }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const send = async (list) => {
    const fd = new FormData();
    [...list].slice(0, 3).forEach((f) => fd.append("photos", f));
    setBusy(true);
    try { await api.post(`/org/submissions/${s.id}/photos`, fd); toast.success("New photo sent"); onDone(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button size="sm" disabled={busy} onClick={() => ref.current?.click()} data-testid={`retake-${s.case_no}`}><Camera size={14} /> {busy ? "Sending…" : "Retake photo"}</Button>
      <input ref={ref} type="file" accept="image/*" capture="environment" multiple hidden onChange={(e) => e.target.files.length && send(e.target.files)} data-testid={`retake-input-${s.case_no}`} />
    </>
  );
}

export function MySubmissions({ api, base, canWrite }) {
  const [list, , reload] = useGet(api, "/org/submissions");
  return (
    <div className={card} data-testid="org-submissions">
      {!list ? <Empty>Loading…</Empty> : !list.length ? <Empty>No submissions yet.</Empty> : list.map((s) => (
        <div key={s.id} className="flex flex-wrap items-center gap-2 border-b border-[var(--lav-2)] py-3 text-sm" data-testid={`org-sub-${s.case_no}`}>
          <b>{s.case_no}</b><span>{s.patient}</span><span className="text-xs text-muted-foreground">{s.phone} · {fmtTime(s.created_at)} · {s.by}</span>
          <span className={`ml-auto rounded-full px-2 py-0.5 text-xs font-bold ${LABEL_CLS[s.label] || "bg-gray-100"}`} data-testid={`org-sub-status-${s.case_no}`}>{s.label}</span>
          {s.label === "Needs new photo" && <p className="w-full text-xs font-semibold text-red-700">{s.reason}{s.note ? ` — ${s.note}` : ""}</p>}
          {s.label === "Needs new photo" && canWrite && <Retake api={api} s={s} onDone={reload} />}
          {s.patient_id && <Link to={`${base}/patients/${s.patient_id}`} className="text-xs font-semibold text-[var(--violet)] underline" data-testid={`org-sub-patient-${s.case_no}`}>Open patient</Link>}
        </div>
      ))}
    </div>
  );
}
